import { useMutation } from '@apollo/client';
import {
  Button,
  Checkbox,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  useToast,
  VStack,
  Flex,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FaTrash } from 'react-icons/fa';
import * as yup from 'yup';
import Input from '../../../../components/Form/Input';
import {
  CREATE_PAGE,
  REMOVE_PAGE,
  UPDATE_PAGE,
} from '../../../../graphQL/mutations';
import { GET_PAGES } from '../../../../graphQL/queries';
import useAuth from '../../../../hooks/useAuth';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (page: any) => void;
  onEdited?: (page: any) => void;
  PageToEdit?: null | any;
}

interface FormData {
  name: string;
  slug?: string;
  header_type?: string;
  show_tab_bar?: boolean;
}

const createFormData = yup.object().shape({
  name: yup.string().required('El nombre es obligatorio'),
});

const DynamicPageModal: React.FC<Props> = ({ isOpen, onClose, PageToEdit }) => {
  const { formState, handleSubmit, register, setValue, reset, control } =
    useForm({
      resolver: yupResolver(createFormData),
    });
  const { t } = useTranslation();
  const toast = useToast();
  const { errors } = formState;
  const [loading, setLoading] = useState(false);

  const [createPage] = useMutation(CREATE_PAGE, {
    refetchQueries: [GET_PAGES, 'GetPages'],
  });

  const [updatePage] = useMutation(UPDATE_PAGE, {
    refetchQueries: [GET_PAGES, 'GetPages'],
  });

  useEffect(() => {
    if (!PageToEdit) return;
    setValue('name', PageToEdit.name);
    setValue('slug', PageToEdit.slug);
    setValue('header_type', PageToEdit.header_type ?? '');
    setValue('show_tab_bar', PageToEdit.show_tab_bar ?? false);
  }, [PageToEdit, setValue]);

  const [removePage, { loading: removeLoading }] = useMutation(REMOVE_PAGE, {
    refetchQueries: [GET_PAGES, 'GetPages'],
  });

  const { nonEditablePages } = useAuth();

  const handlePageDelete = async (pageId: string) => {
    if (nonEditablePages && nonEditablePages?.includes(PageToEdit?.slug)) {
      toast({
        title: 'Opss!!',
        description: t('notPossibleRemoveHomePage'),
        status: 'error',
        position: 'top-right',
      });
    } else {
      try {
        if (window.confirm(t('askDeletePage'))) {
          await removePage({
            variables: {
              id: pageId,
            },
          });
          toast({
            title: t('success'),
            description: t('deletedPage'),
            status: 'success',
            position: 'top-right',
          });
          onClose();
        }
      } catch (err: any) {
        toast({
          title: 'Opss!!',
          description:
            err?.response?.data?.message || err?.message || t('generalError'),
          status: 'error',
          position: 'top-right',
        });
      }
    }
  };

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      setLoading(true);
      if (!PageToEdit) {
        await createPage({
          variables: {
            createPageInput: {
              ...data,
            },
          },
        });

        toast({
          title: t('success'),
          description: t('createdPage'),
          status: 'success',
          position: 'top-right',
        });
      } else if (nonEditablePages?.includes(PageToEdit?.slug)) {
        toast({
          title: 'Opss!!',
          description: t('cannotEditNonEditablePages'),
          status: 'error',
          position: 'top-right',
        });
      } else {
        await updatePage({
          variables: {
            updatePageInput: {
              id: PageToEdit?.id,
              ...data,
            },
          },
        });

        toast({
          title: t('success'),
          description: t('updatedPage'),
          status: 'success',
          position: 'top-right',
        });
      }
      reset();
      onClose();
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description: err?.response?.data?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        reset();
      }}
      isOpen={isOpen}
      isCentered
      size="xl"
    >
      <ModalOverlay />
      <ModalContent
        maxH="90vh"
        as="form"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <ModalHeader>{t('pagesTitlePage')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8}>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Input
                isRequired
                label={t('nameField')}
                error={errors.name as FieldError | undefined}
                {...register('name')}
              />
              <Input
                label={t('headerType')}
                error={errors.header_type as FieldError | undefined}
                {...register('header_type')}
              />
              <Checkbox {...register('show_tab_bar')}>
                {t('showTabBar')}
              </Checkbox>
            </SimpleGrid>
          </VStack>
        </ModalBody>
        <ModalFooter>
          {PageToEdit && !nonEditablePages?.includes(PageToEdit?.slug) && (
            <Flex w="full" justifyContent="flex-start">
              <IconButton
                aria-label="delete-page"
                rounded="md"
                color="#FF6565"
                icon={<FaTrash size={17} />}
                size="xs"
                colorScheme="#FFFFFF"
                isLoading={removeLoading}
                onClick={() => {
                  handlePageDelete(PageToEdit?.id);
                }}
              />
            </Flex>
          )}
          <HStack spacing={4}>
            <Button onClick={onClose}>{t('close')}</Button>
            <Button colorScheme="brand" isLoading={loading} type="submit">
              {t('save')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DynamicPageModal;
